import React from 'react';
import { motion } from 'framer-motion';
import { FileText, CheckCircle, Clock, AlertCircle } from 'lucide-react';

const AssignmentTimeline = ({ assignmentAnalysis }) => {
  if (!assignmentAnalysis || assignmentAnalysis.totalAssignments === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
      >
        <p className="text-gray-500 dark:text-gray-400">No assignment data available</p>
      </motion.div>
    );
  }

  const { totalAssignments, completionRate, averageSubmissionDelay, submissionPattern, recentSubmissions } = assignmentAnalysis;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'early':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'on-time':
        return <Clock className="h-5 w-5 text-blue-600" />;
      case 'late':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      default:
        return <FileText className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'early':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      case 'on-time':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      case 'late':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getPatternBadge = (pattern) => {
    const badges = {
      early: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', text: 'Early Submitter' },
      'on-time': { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', text: 'On-Time' },
      late: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', text: 'Late Submitter' },
      mixed: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300', text: 'Mixed Pattern' }
    };
    return badges[pattern] || badges.mixed;
  };

  const patternBadge = getPatternBadge(submissionPattern);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-orange-600 rounded-lg">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Assignment Timeline</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{totalAssignments} assignments tracked</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${patternBadge.color}`}>
          {patternBadge.text}
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-4 text-center"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Completion Rate</p>
          <p className="text-3xl font-bold text-green-600">{completionRate}%</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4 text-center"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Total Assignments</p>
          <p className="text-3xl font-bold text-blue-600">{totalAssignments}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-lg p-4 text-center"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Avg. Delay (hrs)</p>
          <p className="text-3xl font-bold text-orange-600">{averageSubmissionDelay}</p>
        </motion.div>
      </div>

      {/* Recent Submissions Timeline */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Recent Submissions</h4>
        <div className="space-y-3">
          {recentSubmissions.map((submission, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
            >
              <div className={`p-2 rounded-lg ${getStatusColor(submission.status)}`}>
                {getStatusIcon(submission.status)}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Assignment #{submission.assignmentId?.slice(-6) || index + 1}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Submitted: {new Date(submission.submittedAt).toLocaleDateString()}
                </p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(submission.status)}`}>
                {submission.status}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default AssignmentTimeline;
